import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface ImpersonationContextType {
  personaUserId: string | null;
  isImpersonating: boolean;
  startImpersonation: (userId: string) => void;
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const [personaUserId, setPersonaUserId] = useState<string | null>(null);

  // Read from localStorage on mount
  useEffect(() => {
    const storedUserId = localStorage.getItem('persona_user_id');
    if (storedUserId) {
      setPersonaUserId(storedUserId);
    }
  }, []);

  const startImpersonation = useCallback((userId: string) => {
    localStorage.setItem('persona_user_id', userId);
    setPersonaUserId(userId);
    // Reload to apply header injection
    window.location.reload();
  }, []);

  const stopImpersonation = useCallback(() => {
    localStorage.removeItem('persona_user_id');
    setPersonaUserId(null);
    // Reload to remove header injection
    window.location.reload();
  }, []);

  const isImpersonating = useMemo(() => !!personaUserId, [personaUserId]);

  const value = useMemo(
    () => ({
      personaUserId,
      isImpersonating,
      startImpersonation,
      stopImpersonation,
    }),
    [personaUserId, isImpersonating, startImpersonation, stopImpersonation]
  );

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
}
